import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { useSound } from '../SoundContext';
import { createOnlineTransport } from '../../services/transport/createOnlineTransport';
import { OnlineTransportSession } from '../../types/transport';
import { randomId } from '../../utils/randomId';

export const DEFAULT_EMOTE_SLOTS = ['🤪', '💪', '🤬', '🐷', '🍆'] as const;
export const EMOTE_PICKER_OPTIONS = [
  // Twarze i nastroje
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '🥳', '😊', '🙂', '🙃', '😉', '😍', '🥰', '😘', '🤩', '😎', '🤪',
  '🤓', '🥸', '🤠', '🤡', '😇', '😈', '😤', '😡', '🤬', '😠', '😱', '😳', '🥺', '😭', '😢', '🤯', '😵', '😵‍💫',
  '😒', '🙄', '😏', '🤥', '😶‍🌫️', '🫠', '😑', '🤐', '🤔', '🤨', '🫥', '😴', '🥱', '🤢', '🤮', '🤧', '🤒', '🤕',
  '💀', '☠️', '👻', '🤖', '👺', '👽',

  // Gesty i dłonie
  '👍', '👎', '👏', '🙌', '🫶', '🙏', '🤝', '🤌', '✌️', '🤞', '🤟', '🤘', '👌', '🫵', '👇', '👊', '✊', '👋', '💪',

  // Postacie, role i ciało
  '🤦‍♂️', '🤦‍♀️', '🙈', '👀', '🫣', '🧠',
  '👸', '🤴', '🤶', '🎅', '🧑‍🎄', '👮‍♀️', '👮‍♂️', '🥷', '👩‍🚀', '👨‍🚀', '🧟‍♀️', '🧟‍♂️', '🧟', '🧌',
  '👁️', '🫦', '👄', '👅', '🦵', '🦾', '🦿', '🫀', '🫁',

  // Zwierzęta i stworki
  '🐷', '🐽', '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨', '🐯', '🦁', '🐮', '🐔', '🐣', '🐸', '🐙',
  '🪼', '🦑', '🦀', '🦞', '🦐', '🐠', '🐟', '🐬', '🐳', '🦈', '🐊', '🐺', '🦄', '🐝', '🦋', '🪲', '🐞', '🪰', '🪱',
  '🦠', '🐲', '🐦‍🔥', '🦩',

  // Jedzenie — warzywa i owoce
  '🍆', '🥒', '🌶️', '🌽', '🥕', '🥔', '🥑', '🧄', '🧅', '🍄', '🥦', '🍅',
  '🍇', '🍉', '🍌', '🍍', '🥭', '🍎', '🍏', '🍐', '🍊', '🍋', '🍑', '🍒', '🍓', '🥝',

  // Napoje
  '🍺', '🍻', '🍷', '🥃', '🍸', '🍹', '🍾', '🥂', '☕', '🧋', '🥛', '🥤', '🧃',

  // Symbole, przedmioty i efekty
  '🔥', '⚡', '❄️', '💧', '🌊', '🌈', '🌙', '🌟', '⭐', '☄️', '🌞', '🌪️', '🪐', '🌋', '🌱', '🌿', '🍁',
  '🎯', '🎲', '🪅', '🎉', '🎊', '🎁', '🎈', '🎆', '🎇', '🎮', '🕹️', '🎵', '🎶', '🎸', '🥁', '🎺', '🎻', '🎹', '🎷',
  '🏆', '🥇', '🥈', '🥉', '💣', '🧨', '🧊', '🩸', '💩', '🔮', '🪄', '🩻', '🛠️', '⚙️', '🧰', '🔧', '🔨', '⚒️',
  '🧱', '⚖️', '🔒', '🔑', '🧲', '⚔️', '🔪', '🗿', '🧮', '📎', '🏁', '🏴‍☠️', '🚧', '💤', '🚫', '⛔', '🆘', '📛',
  '🛑', '⁉️', '‼️', '❔', '❓', '☢️',

  // Pojazdy i ruch
  '🚀', '✈️', '🚂', '🚗', '🏍️', '🚲', '🛴', '🛹', '🛺', '🛸', '🪂', '🚁', '🚦', '🏎️',
];

export interface EmoteBurst {
  id: string;
  emoji: string;
  senderId?: string | null;
  senderName?: string | null;
  slot?: number | null;
  ts: number;
}

interface Params {
  gameId?: string;
  playerId?: string | null;
  playerName?: string | null;
  enabled?: boolean;
}

const STORAGE_KEY = 'dice1k_emote_slots';
const MAX_BURSTS = 40;

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export function normalizeSlots(raw: string[] | null | undefined): string[] {
  const normalized: string[] = [];
  for (let i = 0; i < DEFAULT_EMOTE_SLOTS.length; i += 1) {
    const candidate = Array.isArray(raw) ? raw[i] : undefined;
    const emoji = typeof candidate === 'string' && candidate.trim().length > 0 ? candidate.trim() : DEFAULT_EMOTE_SLOTS[i];
    normalized.push(emoji);
  }
  return normalized;
}

const now = () => Date.now();
const burstId = randomId;

function readStoredSlots(raw: string | null): string[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return normalizeSlots(parsed);
    if (isObjectRecord(parsed) && Array.isArray(parsed.slots)) return normalizeSlots(parsed.slots);
  } catch {
    return null;
  }
  return null;
}

function persistSlots(slots: string[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  } catch {}
}

function loadSlots(): string[] {
  if (typeof window === 'undefined') return [...DEFAULT_EMOTE_SLOTS];
  const stored = readStoredSlots(localStorage.getItem(STORAGE_KEY));
  if (stored) return stored;
  return [...DEFAULT_EMOTE_SLOTS];
}

export function useEmoteChannel({ gameId, playerId, playerName, enabled = true }: Params) {
  const [slots, setSlots] = useState<string[]>(() => normalizeSlots(loadSlots()));
  const [bursts, setBursts] = useState<EmoteBurst[]>([]);
  const transportSessionRef = useRef<OnlineTransportSession | null>(null);
  const lastSendRef = useRef<number>(0);
  const slotsRef = useRef<string[]>(slots);
  const transport = useMemo(() => createOnlineTransport(), []);
  const { playSound } = useSound();

  useEffect(() => { slotsRef.current = slots; }, [slots]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSlots(normalizeSlots(loadSlots()));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [playerId]);

  useEffect(() => {
    persistSlots(slots);
  }, [slots]);

  const addBurst = useCallback((incoming: Omit<EmoteBurst, 'id' | 'ts'> & { ts?: number; id?: string }) => {
    const ts = incoming.ts ?? now();
    const id = incoming.id || burstId();
    const payload: EmoteBurst = {
      id,
      ts,
      emoji: incoming.emoji,
      senderId: incoming.senderId,
      senderName: incoming.senderName,
      slot: typeof incoming.slot === 'number' ? incoming.slot : null,
    };
    setBursts((current) => {
      const next = [...current, payload];
      if (next.length > MAX_BURSTS) {
        return next.slice(-MAX_BURSTS);
      }
      return next;
    });
    playSound('emoteSent');
  }, [playSound]);

  useEffect(() => {
    const timer = setInterval(() => {
      setBursts((current) => {
        if (current.length === 0) return current;
        const cutoff = now() - 4000;
        const next = current.filter((burst) => burst.ts >= cutoff);
        return next.length === current.length ? current : next;
      });
    }, 1200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!enabled || !gameId) return;

    let mounted = true;
    let offMessage: (() => void) | null = null;
    let offStatus: (() => void) | null = null;
    void transport.connect(gameId, {
      playerId: playerId || null,
      playerName: playerName || null,
      role: null,
    }).then((session) => {
      if (!mounted) {
        void session.disconnect();
        return;
      }
      transportSessionRef.current = session;
      offMessage = session.onMessage((message) => {
        if (message.type !== 'EMOTE') return;
        if (typeof message.emoji !== 'string' || !message.emoji) return;
        addBurst({
          emoji: message.emoji,
          senderId: typeof message.senderId === 'string' ? message.senderId : null,
          senderName: typeof message.senderName === 'string' ? message.senderName : null,
          slot: typeof message.slot === 'number' ? message.slot : null,
          ts: typeof message.ts === 'number' ? message.ts : now(),
        });
      });
      offStatus = session.onStatus((status: string) => {
        if (isVerboseLoggingEnabled()) {
          logger.debug('EMOTE_TRANSPORT_STATUS', { status, gameId, transport: 'PARTYKIT' });
        }
      });
    }).catch((error: unknown) => {
      if (isVerboseLoggingEnabled()) {
        logger.warn('EMOTE_TRANSPORT_CONNECT_FAILED', { message: getErrorMessage(error) });
      }
    });

    return () => {
      mounted = false;
      if (offMessage) offMessage();
      if (offStatus) offStatus();
      const session = transportSessionRef.current;
      transportSessionRef.current = null;
      if (session) {
        void session.disconnect();
      }
    };
  }, [addBurst, enabled, gameId, playerId, playerName, transport]);

  const setSlotEmoji = useCallback((index: number, emoji: string) => {
    if (index < 0 || index >= DEFAULT_EMOTE_SLOTS.length) return;
    setSlots((current) => {
      const next = [...current];
      next[index] = emoji;
      return normalizeSlots(next);
    });
  }, []);

  const sendEmote = useCallback((slotIndex: number) => {
    if (slotIndex < 0 || slotIndex >= DEFAULT_EMOTE_SLOTS.length) return;
    const nowTs = now();
    const last = lastSendRef.current;
    if (nowTs - last < 200) return; // debounce rapid taps
    lastSendRef.current = nowTs;
    const emoji = slotsRef.current[slotIndex] || DEFAULT_EMOTE_SLOTS[slotIndex] || DEFAULT_EMOTE_SLOTS[0];
    const payload = {
      emoji,
      senderId: playerId || null,
      senderName: playerName || null,
      slot: slotIndex,
      ts: nowTs,
    };
    addBurst(payload);
    const transportSession = transportSessionRef.current;
    if (transportSession && enabled && gameId) {
      transportSession.send({ type: 'EMOTE', emoji, slot: slotIndex }).catch((error: unknown) => {
        if (isVerboseLoggingEnabled()) {
          logger.warn('EMOTE_SEND_FAILED', { transport: 'PARTYKIT', message: getErrorMessage(error) });
        }
      });
    }
  }, [addBurst, enabled, gameId, playerId, playerName]);

  return {
    slots,
    setSlotEmoji,
    sendEmote,
    bursts,
    options: useMemo(() => EMOTE_PICKER_OPTIONS, []),
  };
}
